import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Link, Navigate } from 'react-router-dom';
import { getAdminSession } from '../../utils/adminSession';
import { fetchItemsWithCache } from '../../utils/apiCache';
import { PlayerInfo } from '../../models/PlayerInfo';
import { Fixture } from '../../models/Fixture';
import { Result } from '../../models/Result';
import { entriesUrl, updateStatsUrl, CACHE_KEYS } from '../../utils/apiEndpoints';

const PageContainer = styled.div`
  max-width: 1000px;
  margin: 4rem auto;
  padding: 2rem;
  background-color: var(--gray-900);
  color: var(--gray-50);
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.4);
`;

const Title = styled.h1`
  font-size: 2rem;
  margin-bottom: 1.5rem;
  text-align: center;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.25rem;
  margin-bottom: 0.5rem;
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 1rem;
`;

const Label = styled.label`
  font-weight: 600;
`;

const Input = styled.input`
  padding: 0.6rem 0.8rem;
  border-radius: 6px;
  border: 1px solid #444;
  background: #111;
  color: #f9fafb;

  &::-webkit-calendar-picker-indicator {
    filter: invert(1);
  }
`;

const Select = styled.select`
  padding: 0.6rem 0.8rem;
  border-radius: 6px;
  border: 1px solid #444;
  background: #111;
  color: #f9fafb;
`;

const HelperText = styled.span`
  font-size: 0.8rem;
  color: var(--gray);
`;

const SubmitButton = styled.button`
  margin-top: 1rem;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  border: none;
  background: var(--primary);
  color: #fff;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease, transform 0.1s ease;

  &:hover:enabled {
    background: var(--primary-light);
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Message = styled.div`
  margin-top: 1.5rem;
  padding: 1rem;
  border-radius: 8px;
  background: #12331a;
  color: #c9f7d6;
`;

const ErrorMessage = styled.div`
  margin-top: 1.5rem;
  padding: 1rem;
  border-radius: 8px;
  background: #3a1010;
  color: #ffd1d1;
`;

const ActionsRow = styled.div`
  margin-top: 1rem;
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const SecondaryButton = styled.button`
  padding: 0.6rem 1rem;
  border-radius: 8px;
  border: 1px solid #555;
  background: transparent;
  color: var(--gray-50);
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: #222;
  }
`;

const LinkButton = styled(Link)`
  padding: 0.6rem 1rem;
  border-radius: 8px;
  border: 1px solid #555;
  background: transparent;
  color: var(--gray-50);
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: #222;
  }
`;

const PlayersTable = styled.div`
  margin-top: 0.5rem;
  border-radius: 8px;
  border: 1px solid #333;
  padding: 1rem;
  background: #020617;
`;

const PlayerRow = styled.div`
  display: grid;
  grid-template-columns: 3fr repeat(3, 1fr);
  gap: 0.5rem;
  align-items: center;

  & + & {
    margin-top: 0.5rem;
  }
`;

const GoaliePlayerRow = styled(PlayerRow)`
  grid-template-columns: 3fr repeat(6, 1fr);
`;

const SmallInput = styled(Input)`
  width: 100%;
`;

const AddPlayerButton = styled.button`
  margin-top: 0.75rem;
  padding: 0.5rem 0.75rem;
  border-radius: 6px;
  border: 1px dashed #555;
  background: transparent;
  color: var(--gray-50);
  cursor: pointer;
  font-size: 0.9rem;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;

  &:hover {
    background: #111827;
  }
`;

type SubmitState = 'idle' | 'submitting' | 'success' | 'error';

interface PlayerStatsRow {
  player: string;
  goals: string;
  assists: string;
  pims: string;
  wins: string;
  loses: string;
  so: string;
  ga: string;
  saves: string;
  shotsAgainst: string;
}

interface StatsItem {
  player: string;
}

const MAX_PLAYERS = 22;
const INITIAL_ROWS = 6;

const TigersTeamName = 'Sheffield Ice Tigers';

const toTitleCase = (value: string): string => {
  if (!value) return value;

  return value
    .trim()
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const GameSheet: React.FC = () => {
  const [homeTeam, setHomeTeam] = useState('');
  const [awayTeam, setAwayTeam] = useState(TigersTeamName);
  const [fixtureOptions, setFixtureOptions] = useState<Fixture[]>([]);
  const [loadingFixtures, setLoadingFixtures] = useState(false);
  const [selectedFixtureKey, setSelectedFixtureKey] = useState<string>('');
  const [homeScore, setHomeScore] = useState('');
  const [awayScore, setAwayScore] = useState('');
  const [date, setDate] = useState('');
  const [venue, setVenue] = useState('');
  const [motm, setMotm] = useState('');
  const [tigersHat, setTigersHat] = useState('');
  const [players, setPlayers] = useState<string[]>([]);
  const [playerPositionsByName, setPlayerPositionsByName] = useState<Record<string, string>>({});
  const [playerRows, setPlayerRows] = useState<PlayerStatsRow[]>(
    Array.from({ length: INITIAL_ROWS }, () => ({
      player: '',
      goals: '',
      assists: '',
      pims: '',
      wins: '',
      loses: '',
      so: '',
      ga: '',
      saves: '',
      shotsAgainst: '',
    }))
  );
  const [submitState, setSubmitState] = useState<SubmitState>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [loadingPlayers, setLoadingPlayers] = useState(false);

  const session = getAdminSession();
  const apiKey = session?.apiKey ?? null;

  const normalizeName = (name: string) => name.trim().toLowerCase();
  const fixtureKey = (f: Fixture) => `${normalizeName(String(f.homeTeam ?? ''))}|${f.date}|${f.time}`;

  const isGoaliePlayer = (playerName: string) => {
    if (!playerName) return false;
    const pos = playerPositionsByName[normalizeName(playerName)] ?? '';
    return pos.split('/').some((p) => p.trim() === 'Goalie');
  };

  useEffect(() => {
    const fetchPlayers = async () => {
      try {
        if (!apiKey) return;
        setLoadingPlayers(true);
        const CACHE_TTL_MS = 5 * 60 * 1000;

        const [skaterStatsItems, goalieStatsItems, playersItems] = await Promise.all([
          fetchItemsWithCache<StatsItem>(entriesUrl('tigers_stats'), CACHE_KEYS.stats, CACHE_TTL_MS),
          fetchItemsWithCache<StatsItem>(
            entriesUrl('tigers_goalie_stats'),
            CACHE_KEYS.goalieStats,
            CACHE_TTL_MS
          ),
          fetchItemsWithCache<PlayerInfo>(entriesUrl('tigers_players'), CACHE_KEYS.players, CACHE_TTL_MS),
        ]);

        const names = Array.from(
          new Set([
            ...(skaterStatsItems ?? []).map((item) => item.player).filter(Boolean),
            ...(goalieStatsItems ?? []).map((item) => item.player).filter(Boolean),
          ])
        ).sort((a, b) => a.localeCompare(b));
        setPlayers(names);

        const posMap: Record<string, string> = {};
        (playersItems ?? []).forEach((p) => {
          if (!p?.name) return;
          posMap[normalizeName(p.name)] = p.position ?? '';
        });
        setPlayerPositionsByName(posMap);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching players', error);
        setErrorMessage('Unable to load players for game sheet.');
      } finally {
        setLoadingPlayers(false);
      }
    };

    fetchPlayers();
  }, [apiKey]);

  useEffect(() => {
    const fetchFixturesForGameSheet = async () => {
      try {
        if (!apiKey) return;
        setLoadingFixtures(true);

        const CACHE_TTL_MS = 5 * 60 * 1000;

        const [fixturesItems, resultsItems] = await Promise.all([
          fetchItemsWithCache<Fixture>(
            entriesUrl('tigers_fixtures'),
            CACHE_KEYS.fixturesGameSheetFixtures,
            CACHE_TTL_MS
          ),
          fetchItemsWithCache<Result>(
            entriesUrl('tigers_results'),
            CACHE_KEYS.resultsGameSheetFixtures,
            CACHE_TTL_MS
          ),
        ]);

        const fixtureKeyInner = (f: Fixture) =>
          `${normalizeName(String(f.homeTeam ?? ''))}|${f.date}|${f.time}`;

        const resultKeys = new Set(
          (resultsItems ?? []).map((r) => `${normalizeName(String(r.homeTeam ?? ''))}|${r.date}|${r.time}`)
        );

        const eligibleFixtures = (fixturesItems ?? [])
          .filter((f) => !resultKeys.has(fixtureKeyInner(f)))
          .map((f) => {
            const dt = new Date(`${f.date}T${f.time}`);
            return { fixture: f, dt };
          })
          .filter((x) => !Number.isNaN(x.dt.getTime()))
          .sort((a, b) => a.dt.getTime() - b.dt.getTime())
          .map((x) => x.fixture);

        setFixtureOptions(eligibleFixtures);

        if (eligibleFixtures.length > 0) {
          const first = eligibleFixtures[0];
          setSelectedFixtureKey(fixtureKeyInner(first));
          setHomeTeam(first.homeTeam ?? '');
          setAwayTeam(first.awayTeam ?? '');
          setDate(first.date ?? '');
          setVenue(first.venue ?? '');
        } else {
          setSelectedFixtureKey('');
          setHomeTeam('');
          setAwayTeam('');
          setDate('');
          setVenue('');
        }
      } catch {
        setFixtureOptions([]);
        setSelectedFixtureKey('');
      } finally {
        setLoadingFixtures(false);
      }
    };

    fetchFixturesForGameSheet();
  }, [apiKey]);

  if (!session) {
    return <Navigate to="/login" replace />;
  }

  const handlePlayerChange = (index: number, field: keyof PlayerStatsRow, value: string) => {
    setPlayerRows((rows) => rows.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
  };

  const handleAddPlayerRow = () => {
    setPlayerRows((rows) => {
      if (rows.length >= MAX_PLAYERS) return rows;
      return [
        ...rows,
        {
          player: '',
          goals: '',
          assists: '',
          pims: '',
          wins: '',
          loses: '',
          so: '',
          ga: '',
          saves: '',
          shotsAgainst: '',
        },
      ];
    });
  };

  const resetForm = () => {
    setHomeTeam('');
    setAwayTeam(TigersTeamName);
    setHomeScore('');
    setAwayScore('');
    setSelectedFixtureKey('');
    setDate('');
    setVenue('');
    setMotm('');
    setTigersHat('');
    setPlayerRows(
      Array.from({ length: INITIAL_ROWS }, () => ({
        player: '',
        goals: '',
        assists: '',
        pims: '',
        wins: '',
        loses: '',
        so: '',
        ga: '',
        saves: '',
        shotsAgainst: '',
      }))
    );
    setSubmitState('idle');
    setErrorMessage(null);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    setSubmitState('submitting');
    setErrorMessage(null);

    try {
      const playerStats = playerRows
        .filter((row) => row.player)
        .map((row) => {
          if (isGoaliePlayer(row.player)) {
            return {
              player: row.player,
              wins: Number(row.wins || 0),
              losses: Number(row.loses || 0),
              so: Number(row.so || 0),
              ga: Number(row.ga || 0),
              saves: Number(row.saves || 0),
              shotsAgainst: Number(row.shotsAgainst || 0),
            };
          }

          return {
            player: row.player,
            goals: Number(row.goals || 0),
            assists: Number(row.assists || 0),
            pims: Number(row.pims || 0),
          };
        });

      const normalizedHomeTeam = toTitleCase(homeTeam);
      const normalizedAwayTeam = toTitleCase(awayTeam);
      const normalizedVenue = toTitleCase(venue);
      const normalizedMotm = toTitleCase(motm);
      const normalizedTigersHat = toTitleCase(tigersHat);

      const payload = {
        teamName: 'tigers',
        homeTeam: normalizedHomeTeam,
        awayTeam: normalizedAwayTeam,
        homeScore: Number(homeScore || 0),
        awayScore: Number(awayScore || 0),
        date,
        venue: normalizedVenue,
        motm: normalizedMotm,
        tigersHat: normalizedTigersHat,
        playerStats,
      };

      const response = await fetch(updateStatsUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': session.apiKey,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || 'Failed to submit game sheet');
      }

      setSubmitState('success');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error while submitting game sheet';
      setErrorMessage(message);
      setSubmitState('error');
    }
  };

  if (submitState === 'success') {
    return (
      <PageContainer>
        <Title>Game Sheet Submitted</Title>
        <Message>The game sheet was submitted successfully.</Message>
        <ActionsRow>
          <SecondaryButton type="button" onClick={resetForm}>
            Submit another game
          </SecondaryButton>
          <LinkButton to="/admin">Back to admin</LinkButton>
        </ActionsRow>
      </PageContainer>
    );
  }

  if (submitState === 'error') {
    return (
      <PageContainer>
        <Title>Game Sheet Not Submitted</Title>
        <ErrorMessage>
          There was a problem submitting the game sheet.
          {errorMessage ? <div style={{ marginTop: '0.5rem' }}>{errorMessage}</div> : null}
        </ErrorMessage>
        <ActionsRow>
          <SecondaryButton type="button" onClick={resetForm}>
            Try again
          </SecondaryButton>
          <LinkButton to="/admin">Back to admin</LinkButton>
        </ActionsRow>
      </PageContainer>
    );
  }

  const usedPlayers = (currentIndex: number) =>
    new Set(
      playerRows.map((row, index) => (index === currentIndex ? null : row.player)).filter((name): name is string => !!name)
    );

  return (
    <PageContainer>
      <Title>Submit Game Sheet</Title>
      <Form onSubmit={handleSubmit}>
        <SectionTitle>Game Details</SectionTitle>
        <FieldGrid>
          <FieldGroup>
            <Label htmlFor="fixture">Fixture</Label>
            <Select
              id="fixture"
              value={selectedFixtureKey}
              onChange={(e) => {
                const key = e.target.value;
                setSelectedFixtureKey(key);
                const fixture = fixtureOptions.find((f) => fixtureKey(f) === key) ?? null;
                if (fixture) {
                  setHomeTeam(fixture.homeTeam ?? '');
                  setAwayTeam(fixture.awayTeam ?? '');
                  setDate(fixture.date ?? '');
                  setVenue(fixture.venue ?? '');
                }
              }}
              required
              disabled={loadingFixtures || fixtureOptions.length === 0}
            >
              {loadingFixtures ? (
                <option value="" disabled>
                  Loading fixtures…
                </option>
              ) : (
                fixtureOptions.map((f) => {
                  const key = fixtureKey(f);
                  const label = `${f.homeTeam} vs ${f.awayTeam} - ${f.date} ${f.time} (${f.venue})`;
                  return (
                    <option key={key} value={key}>
                      {label}
                    </option>
                  );
                })
              )}
            </Select>
          </FieldGroup>

          <FieldGroup>
            <Label htmlFor="homeTeam">Home Team</Label>
            <Input id="homeTeam" type="text" value={homeTeam} disabled />
          </FieldGroup>

          <FieldGroup>
            <Label htmlFor="awayTeam">Away Team</Label>
            <Input id="awayTeam" type="text" value={awayTeam} disabled />
          </FieldGroup>

          <FieldGroup>
            <Label htmlFor="venue">Venue</Label>
            <Input
              id="venue"
              type="text"
              value={venue}
              onChange={(e) => setVenue(e.target.value)}
              required
              disabled={!!selectedFixtureKey}
            />
          </FieldGroup>

          <FieldGroup>
            <Label htmlFor="date">Date</Label>
            <Input
              id="date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              required
              disabled={!!selectedFixtureKey}
            />
            <HelperText>Format: YYYY-MM-DD</HelperText>
          </FieldGroup>

          <FieldGroup>
            <Label htmlFor="homeScore">Home Score</Label>
            <Input
              id="homeScore"
              type="number"
              min="0"
              value={homeScore}
              onChange={(e) => setHomeScore(e.target.value)}
              required
            />
          </FieldGroup>

          <FieldGroup>
            <Label htmlFor="awayScore">Away Score</Label>
            <Input
              id="awayScore"
              type="number"
              min="0"
              value={awayScore}
              onChange={(e) => setAwayScore(e.target.value)}
              required
            />
          </FieldGroup>
        </FieldGrid>

        <SectionTitle>Awards</SectionTitle>
        <FieldGrid>
          <FieldGroup>
            <Label htmlFor="motm">Man of the Match</Label>
            <Select id="motm" value={motm} onChange={(e) => setMotm(e.target.value)} disabled={loadingPlayers} required>
              <option value="">Select player</option>
              {players.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </Select>
          </FieldGroup>

          <FieldGroup>
            <Label htmlFor="tigersHat">Tigers Hat</Label>
            <Select
              id="tigersHat"
              value={tigersHat}
              onChange={(e) => setTigersHat(e.target.value)}
              disabled={loadingPlayers}
              required
            >
              <option value="">Select player</option>
              {players.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </Select>
          </FieldGroup>
        </FieldGrid>

        <SectionTitle>Player Stats</SectionTitle>
        <PlayersTable>
          {loadingPlayers && <HelperText>Loading players…</HelperText>}
          {!loadingPlayers &&
            playerRows.map((row, index) => {
              const used = usedPlayers(index);
              const availablePlayers = players.filter((name) => !used.has(name) || row.player === name);
              const goalieRow = isGoaliePlayer(row.player);

              return goalieRow ? (
                <GoaliePlayerRow key={index}>
                  <Select value={row.player} onChange={(e) => handlePlayerChange(index, 'player', e.target.value)}>
                    <option value="">Select player</option>
                    {availablePlayers.map((name) => (
                      <option key={name} value={name}>
                        {name}
                      </option>
                    ))}
                  </Select>
                  <SmallInput
                    type="number"
                    min="0"
                    placeholder="Wins"
                    value={row.wins}
                    onChange={(e) => handlePlayerChange(index, 'wins', e.target.value)}
                  />
                  <SmallInput
                    type="number"
                    min="0"
                    placeholder="Loses"
                    value={row.loses}
                    onChange={(e) => handlePlayerChange(index, 'loses', e.target.value)}
                  />
                  <SmallInput
                    type="number"
                    min="0"
                    placeholder="SO"
                    value={row.so}
                    onChange={(e) => handlePlayerChange(index, 'so', e.target.value)}
                  />
                  <SmallInput
                    type="number"
                    min="0"
                    placeholder="GA"
                    value={row.ga}
                    onChange={(e) => handlePlayerChange(index, 'ga', e.target.value)}
                  />
                  <SmallInput
                    type="number"
                    min="0"
                    placeholder="Saves"
                    value={row.saves}
                    onChange={(e) => handlePlayerChange(index, 'saves', e.target.value)}
                  />
                  <SmallInput
                    type="number"
                    min="0"
                    placeholder="Shots Against"
                    value={row.shotsAgainst}
                    onChange={(e) => handlePlayerChange(index, 'shotsAgainst', e.target.value)}
                  />
                </GoaliePlayerRow>
              ) : (
                <PlayerRow key={index}>
                  <Select value={row.player} onChange={(e) => handlePlayerChange(index, 'player', e.target.value)}>
                    <option value="">Select player</option>
                    {availablePlayers.map((name) => (
                      <option key={name} value={name}>
                        {name}
                      </option>
                    ))}
                  </Select>
                  <SmallInput
                    type="number"
                    min="0"
                    placeholder="Goals"
                    value={row.goals}
                    onChange={(e) => handlePlayerChange(index, 'goals', e.target.value)}
                  />
                  <SmallInput
                    type="number"
                    min="0"
                    placeholder="Assists"
                    value={row.assists}
                    onChange={(e) => handlePlayerChange(index, 'assists', e.target.value)}
                  />
                  <SmallInput
                    type="number"
                    min="0"
                    placeholder="PIMs"
                    value={row.pims}
                    onChange={(e) => handlePlayerChange(index, 'pims', e.target.value)}
                  />
                </PlayerRow>
              );
            })}

          {playerRows.length < MAX_PLAYERS && (
            <AddPlayerButton type="button" onClick={handleAddPlayerRow}>
              + Add another player
            </AddPlayerButton>
          )}
        </PlayersTable>

        <SubmitButton type="submit" disabled={submitState === 'submitting' || loadingFixtures || !selectedFixtureKey}>
          {submitState === 'submitting' ? 'Submitting…' : 'Submit Game Sheet'}
        </SubmitButton>
        <ActionsRow>
          <LinkButton to="/admin">Back to admin</LinkButton>
        </ActionsRow>
      </Form>
    </PageContainer>
  );
};

export default GameSheet;

